/**
 * Simple in-memory cache for database queries with TTL support
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class QueryCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached data if it exists and hasn't expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data with optional TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL,
    });
  }

  /**
   * Clear specific cache entry
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const queryCache = new QueryCache();

// Cleanup expired entries every 10 minutes
setInterval(() => {
  queryCache.cleanup();
}, 10 * 60 * 1000);

/**
 * Generate cache key for player stats
 */
export function getPlayerStatsCacheKey(bowlerId: number, seasonYear: number): string {
  return `player_stats_${bowlerId}_${seasonYear}`;
}

/**
 * Generate cache key for league averages
 */
export function getLeagueAveragesCacheKey(seasonYear: number, excludeBowlerId?: number): string {
  return `league_averages_${seasonYear}_${excludeBowlerId || 'all'}`;
}

/**
 * Generate cache key for weekly averages
 */
export function getWeeklyAveragesCacheKey(bowlerId: number, seasonYear: number): string {
  return `weekly_averages_${bowlerId}_${seasonYear}`;
}

/**
 * Generate cache key for lane performance
 */
export function getLanePerformanceCacheKey(bowlerId: number, seasonYear: number): string {
  return `lane_performance_${bowlerId}_${seasonYear}`;
}

/**
 * Generate cache key for game position performance
 */
export function getGamePositionCacheKey(bowlerId: number, seasonYear: number): string {
  return `game_position_${bowlerId}_${seasonYear}`;
}

/**
 * Generate cache key for player list
 */
export function getPlayerListCacheKey(): string {
  return 'player_list';
}